import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuickStart } from '@hooks/register/screening/use-quick-start';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { useSession } from '@hooks/security/use-session';
import { asString, asStringBool } from '@/utils/parse';
import { createQuickStartSchema } from '@views/register/screening/quick-start.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IQuickStartFormValues, IQuickStartModel, IQuickStartSubmitResult } from '@/types/screening/quick-start';
import PageLoader from '@components/page-loader';
import FieldError from '@components/field-error';
import StepFormLayout from '@/components/step-form-layout';

const QuickStart = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useQuickStart(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <QuickStartContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IQuickStartContentProps {
    model: IQuickStartModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IQuickStartFormValues) => Promise<IQuickStartSubmitResult>;
}

const QuickStartContent = ({ model, isSubmitting, submitError, onSubmit }: IQuickStartContentProps) => {
    const t = useLocalize(model.languageData);

    const schema = useMemo(
        () =>
            createQuickStartSchema({
                needsName: model.needsName,
                showOfferToConnectToDonor: model.showOfferToConnectToDonor,
                isDonorConnectNegC: model.isDonorConnectNegC,
            }),
        [model.needsName, model.showOfferToConnectToDonor, model.isDonorConnectNegC],
    );

    const form = useStepForm<IQuickStartFormValues, IQuickStartSubmitResult>({
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: model.firstName,
            lastName: model.lastName,
            gender: asString(model.gender),
            birthMonth: asString(model.birthMonth),
            birthDay: asString(model.birthDay),
            birthYear: asString(model.birthYear),
            hasConfirmedBirthDateMatchesId: model.birthMonth != null && model.birthDay != null && model.birthYear != null,
            heightFeet: asString(model.heightFeet),
            heightInches: asString(model.heightInches),
            weightLbs: asString(model.weightLbs),
            optOutOfDonorConnect: model.optOutOfDonorConnect === true,
            connectToDonorIsDesired: asStringBool(model.connectToDonorIsDesired),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        register,
        formState: { errors },
    } = form;

    let donorMentorQuestion = t(
        'DashLabel_DonorConnectOptInQuestion',
        'Would you like us to connect you with a living donor mentor who can answer your questions and help you better understand the process?',
    );

    if (model.isLiver) {
        donorMentorQuestion = donorMentorQuestion.replace('kidney', 'liver');
    }

    return (
        <>
            <StepFormLayout
                kind="screening"
                model={model}
                step={DashRegistrationStep.QuickStart}
                title={'Demographic Information'}
                t={t}
                form={form}
                isSubmitting={isSubmitting}
                submitError={submitError}
            >
                <>
                    <div className="row subtitle">
                        <div className="col-xs-12">
                            <h4>
                                {t(
                                    'DashLabel_ItShouldTakeLessThan5MinutesToAnswerSomeScreening',
                                    'It should take less than 5 minutes to answer some screening questions',
                                )}
                            </h4>
                        </div>
                    </div>

                    {model.needsName && (
                        <>
                            <div className="form-group">
                                <label className="control-label col-xs-12" htmlFor="FirstName">
                                    <span className="form-required">*</span>{' '}
                                    {t('DashLabel_LegalFirstName', 'Legal First Name')}{' '}
                                    <span className="label label-default">
                                        {t(
                                            'DashLabel_AsItAppearsOnStateIssuedId',
                                            'as it appears on State-Issued ID',
                                        )}
                                    </span>
                                    :<FieldError message={errors.firstName?.message} />
                                </label>
                                <div className="col-xs-12 col-sm-8">
                                    <input
                                        id="FirstName"
                                        className="form-control trim-field"
                                        maxLength={32}
                                        {...register('firstName')}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="control-label col-xs-12" htmlFor="LastName">
                                    <span className="form-required">*</span>{' '}
                                    {t('DashLabel_LegalLastName', 'Legal Last Name')}{' '}
                                    <span className="label label-default">
                                        {t(
                                            'DashLabel_AsItAppearsOnStateIssuedId',
                                            'as it appears on State-Issued ID',
                                        )}
                                    </span>
                                    :<FieldError message={errors.lastName?.message} />
                                </label>
                                <div className="col-xs-12 col-sm-8">
                                    <input
                                        id="LastName"
                                        className="form-control trim-field"
                                        maxLength={32}
                                        {...register('lastName')}
                                    />
                                </div>
                            </div>

                        </>
                    )}

                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="Gender">
                            <span className="form-required">*</span> {t('Word_Gender', 'Gender')}
                            <FieldError message={errors.gender?.message} />
                        </label>
                        <div className="col-xs-12 col-sm-8">
                            <label className="radio-inline">
                                <input type="radio" value='1' {...register('gender')} />{' '}
                                {t('Word_Male', 'Male')}
                            </label>
                            <label className="radio-inline">
                                <input type="radio" value='2' {...register('gender')} />{' '}
                                {t('Word_Female', 'Female')}
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <div className="col-xs-12 col-md-8">
                            <div className="birth-date-panel">
                                <div className="row">
                                    <label className="control-label col-xs-12" htmlFor="BirthDate">
                                        <span className="form-required">*</span>{' '}
                                        {t('DashLabel_BirthDate', 'Date of Birth')}:
                                        <FieldError message={errors.birthDate?.message} />
                                    </label>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12 col-sm-4">
                                        <select className="form-control" {...register('birthMonth', { deps: ['birthDate'] })}>
                                            <option value="">
                                                {t('DashLabel_PleaseSelect', 'Please Select')}
                                            </option>
                                            {model.birthMonthList.map((birthMonth, index) => (
                                                <option key={'bm-' + index} value={birthMonth.value}>
                                                    {birthMonth.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-xs-12 col-sm-4">
                                        <select className="form-control" {...register('birthDay', { deps: ['birthDate'] })}>
                                            <option value="">
                                                {t('DashLabel_PleaseSelect', 'Please Select')}
                                            </option>
                                            {model.birthDayList.map((birthDay, index) => (
                                                <option key={'bd-' + index} value={birthDay.value}>
                                                    {birthDay.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-xs-12 col-sm-4">
                                        <select className="form-control" {...register('birthYear', { deps: ['birthDate'] })}>
                                            <option value="">
                                                {t('DashLabel_PleaseSelect', 'Please Select')}
                                            </option>
                                            {model.birthYearList.map((birthYear, index) => (
                                                <option key={'by-' + index} value={birthYear.value}>
                                                    {birthYear.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12">
                                        <div className="birth-date-note">
                                            <span
                                                className="glyphicon glyphicon-info-sign"
                                                style={{ flexShrink: 0, marginTop: 2 }}
                                            ></span>
                                            <div>
                                                {t(
                                                    'DashLabel_BirthDateMatchesOfficialIdNote',
                                                    'Please make sure the date of birth above matches your official ID. This helps us ensure accuracy and protect your information.',
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-xs-12">
                                        <FieldError
                                            message={errors.hasConfirmedBirthDateMatchesId?.message}
                                            block
                                        />
                                        <div className="checkbox birth-date-ack">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    {...register('hasConfirmedBirthDateMatchesId')}
                                                />{' '}
                                                {t(
                                                    'DashLabel_BirthDateMatchesStateIdAcknowledgement',
                                                    'I acknowledge that my Date of Birth is accurate and matches what is on my State-Issued ID',
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="HeightFeet">
                            <span className="form-required">*</span> {t('Word_Height', 'Height')}
                        </label>
                        <div className="col-xs-12 col-sm-5 col-md-4">
                            <div className="input-group">
                                <select className="form-control" {...register('heightFeet')}>
                                    <option value="">
                                        {t('DashLabel_PleaseSelect', 'Please Select')}
                                    </option>
                                    {[4, 5, 6, 7].map((feet) => (
                                        <option key={feet} value={feet}>
                                            {feet}
                                        </option>
                                    ))}
                                </select>
                                <span className="input-group-addon">
                                    <div style={{ width: 23 }}>ft</div>
                                </span>
                            </div>
                            <label><FieldError message={errors.heightFeet?.message} /></label>
                        </div>
                        <div className="col-xs-12 col-sm-5 col-md-4">
                            <div className="input-group">
                                <select className="form-control" {...register('heightInches')}>
                                    <option value="">
                                        {t('DashLabel_PleaseSelect', 'Please Select')}
                                    </option>
                                    {Array.from({ length: 12 }, (_, i) => i).map((inch) => (
                                        <option key={inch} value={inch}>
                                            {inch}
                                        </option>
                                    ))}
                                </select>
                                <span className="input-group-addon">
                                    <div style={{ width: 23 }}>in</div>
                                </span>
                            </div>
                            <label><FieldError message={errors.heightInches?.message} /></label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="control-label col-xs-12" htmlFor="WeightLbs">
                            <span className="form-required">*</span> {t('Word_Weight', 'Weight')}
                            <FieldError message={errors.weightLbs?.message} />
                        </label>
                        <div className="col-xs-12 col-sm-4 col-md-3">
                            <div className="input-group">
                                <input
                                    id="WeightLbs"
                                    className="form-control"
                                    maxLength={11}
                                    {...register('weightLbs')}
                                />
                                <span className="input-group-addon">
                                    <div style={{ width: 23 }}>lbs</div>
                                </span>
                            </div>
                        </div>
                    </div>

                    {model.showOfferToConnectToDonor &&
                        (model.isDonorConnectNegC ? (
                            <div className="form-group" style={{ marginTop: 20 }}>
                                <div className="col-xs-12">
                                    <p>
                                        {t(
                                            'DashLabel_ToHelpYouThroughTheDonationProcessWeWillBeAssignin',
                                            'Once you have successfully completed the screening process and been approved to continue to the next step, we will assign you to a donor mentor, who should reach out to you within 2 days of your completed screening. Donor mentors are living donors themselves, so they will be able to explain the process and answer your questions',
                                        )}
                                        .
                                    </p>
                                    <label
                                        className="control-label col-xs-12"
                                        htmlFor="OptOut"
                                        style={{ fontWeight: 'normal' }}
                                    >
                                        {t(
                                            'DashLabel_IfYouDoNotWantToBeContactedByADonorMentorYouMayOpt',
                                            'If you do not want to be contacted by a Donor Mentor, you may opt out here -',
                                        )}{' '}
                                        <strong>
                                            <input
                                                type="checkbox"
                                                {...register('optOutOfDonorConnect')}
                                            />{' '}
                                            {t('Word_OptOut', 'opt-out')}.
                                        </strong>
                                    </label>
                                </div>
                            </div>
                        ) : (
                            <div className='form-group'>
                                <div className="col-xs-12 col-md-8">
                                    <div className="donor-mentor-card">
                                        <h4 className="donor-mentor-title">
                                            {t(
                                                'DashLabel_DonorMentorIsHere',
                                                'A Donor Mentor is Here for You Throughout Your Donation Journey',
                                            )}
                                        </h4>

                                        <div style={{ padding: '56.25% 0 30px 0', position: 'relative' }}>
                                            <iframe
                                                src="https://player.vimeo.com/video/1143834262?h=c2c754eea1&badge=0&autopause=0&player_id=0&app_id=58479"
                                                frameBorder="0"
                                                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                                referrerPolicy="strict-origin-when-cross-origin"
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                }}
                                                title="Donor Mentors"
                                            ></iframe>
                                        </div>

                                        <p
                                            className="donor-mentor-text"
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'DashLabel_DonorMentorSupport',
                                                    "Your decision to consider donation is a remarkable act of kindness. To support you in your donation journey, <strong style='color:black'> the NKR offers <font style='color: var(--red) !important'>free</font> access to a donor mentor. </strong>",
                                                ),
                                            }}
                                        />
                                        <p className="donor-mentor-text">
                                            {t(
                                                'DashNotice_DonorMentorDescription',
                                                'Donor mentors, who are living donors themselves, can answer questions, address concerns, and guide you through every step of the process. With their firsthand experience, donor mentors provide a wealth of information, support, and education to ensure your donation goes as smoothly as possible.',
                                            )}
                                        </p>

                                        <div className="form-group donor-mentor-question">
                                            <label className="control-label">
                                                {donorMentorQuestion}
                                                <FieldError
                                                    message={errors.connectToDonorIsDesired?.message}
                                                />
                                            </label>

                                            <div className="donor-mentor-radios">
                                                <label className="radio-inline">
                                                    <input
                                                        type="radio"
                                                        id="ConnectYes"
                                                        value="1"
                                                        {...register('connectToDonorIsDesired')}
                                                    />{' '}
                                                    {t('Word_Yes', 'Yes')}
                                                </label>
                                                <label className="radio-inline">
                                                    <input
                                                        type="radio"
                                                        id="ConnectNo"
                                                        value="0"
                                                        {...register('connectToDonorIsDesired')}
                                                    />{' '}
                                                    {t('Label_NotAtThisTime', 'Not at this time')}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                        )}
                </>
            </StepFormLayout>
        </>
    );
};

export default QuickStart;
